import { useRef, useState } from "react";
import { BackRow } from "../forms/Modal.tsx";
import { parseCustomPalette, paletteToTemplateJson, saveTheme, applyTheme, THEMES, type Palette } from "../themes.ts";

const COLOUR_KEYS = [
  { key: "obsidian", desc: "Deepest background" },
  { key: "obsidian2", desc: "Slightly lighter background" },
  { key: "panel", desc: "Card/section background" },
  { key: "panel2", desc: "Elevated card background" },
  { key: "edge", desc: "Borders and dividers" },
  { key: "cream", desc: "Primary text" },
  { key: "creamDim", desc: "Secondary text" },
  { key: "muted", desc: "Muted/tertiary text" },
  { key: "mutedLight", desc: "Disabled text" },
  { key: "amber", desc: "Primary accent" },
  { key: "amberHot", desc: "Hot accent (pressed states)" },
  { key: "cyan", desc: "Data accent (charts)" },
  { key: "redline", desc: "Danger/error colour" },
  { key: "lime", desc: "Positive/success accent" },
] as const;

export function CustomThemeView({ isPro, requirePro, theme, setTheme, onBack }: {
  isPro: boolean; requirePro?: () => void; theme: Palette; setTheme: (p: Palette) => void; onBack: () => void;
}) {
  const importRef = useRef<HTMLInputElement>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  if (!isPro) {
    return (
      <div className="screen">
        <div className="container glow">
          <BackRow onClick={onBack} />
          <div className="screen-title">CUSTOM THEME</div>
          <div className="panel" style={{ marginTop: 14 }}>
            <p className="mono" style={{ color: "var(--creamDim)", fontSize: 13, lineHeight: 1.6 }}>
              Create your own colour theme by exporting a template, editing the hex values, and importing it back.
            </p>
            <button className="btn primary" style={{ marginTop: 18 }} onClick={requirePro}>UNLOCK PRO</button>
          </div>
        </div>
      </div>
    );
  }

  const handleExport = () => {
    const json = paletteToTemplateJson(theme.name === "custom" ? theme : (THEMES[0] ?? theme));
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${new Date().toISOString().slice(0, 10)}-carspend-theme-template.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file: File) => {
    setImportError(null);
    setImportSuccess(false);
    try {
      const text = await file.text();
      const parsed = parseCustomPalette(text);
      if (!parsed) {
        setImportError("Invalid theme file. Check the format.");
        return;
      }
      setTheme(parsed);
      saveTheme(parsed);
      applyTheme(parsed);
      setImportSuccess(true);
    } catch {
      setImportError("Failed to read file.");
    }
  };

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">CUSTOM THEME</div>

        <p className="mono" style={{ color: "var(--creamDim)", fontSize: 12, lineHeight: 1.6, marginTop: 4 }}>
          Export a theme template, edit the hex colour values in any text editor, then import it back.
          The palette has 14 colours — each one controls a different part of the app's appearance.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
          <button className="panel" style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, cursor: "pointer", textAlign: "left", width: "100%" }} onClick={handleExport}>
            <span style={{ color: "var(--amber)", fontSize: 18 }}>↑</span>
            <div>
              <div className="mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "var(--cream)" }}>EXPORT THEME TEMPLATE</div>
              <div className="mono muted" style={{ fontSize: 9, marginTop: 2 }}>Downloads a .json file you can edit</div>
            </div>
          </button>

          <input ref={importRef} type="file" accept="application/json,.json" hidden
            onChange={async (e) => { const f = e.target.files?.[0]; if (f) await handleImport(f); e.target.value = ""; }} />
          <button className="panel" style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, cursor: "pointer", textAlign: "left", width: "100%" }} onClick={() => importRef.current?.click()}>
            <span style={{ color: "var(--amber)", fontSize: 18 }}>↓</span>
            <div>
              <div className="mono" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "var(--cream)" }}>IMPORT CUSTOM THEME</div>
              <div className="mono muted" style={{ fontSize: 9, marginTop: 2 }}>Select a .json theme file to apply</div>
            </div>
          </button>
        </div>

        {importSuccess && (
          <div className="panel" style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, borderColor: "var(--lime)" }}>
            <span style={{ color: "var(--lime)", fontSize: 14 }}>✓</span>
            <span className="mono" style={{ fontSize: 11, color: "var(--lime)" }}>Custom theme active</span>
          </div>
        )}

        {importError && (
          <div className="panel" style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, borderColor: "var(--redline)" }}>
            <span style={{ color: "var(--redline)", fontSize: 14 }}>⚠</span>
            <span className="mono" style={{ fontSize: 11, color: "var(--redline)" }}>{importError}</span>
          </div>
        )}

        <div className="divider" style={{ marginTop: 18 }}><span className="label">COLOUR REFERENCE</span><span className="line" /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {COLOUR_KEYS.map(({ key, desc }) => (
            <div key={key} className="panel" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px" }}>
              <span className="mono" style={{ fontSize: 10, color: "var(--creamDim)" }}>{key}</span>
              <span className="mono muted" style={{ fontSize: 9 }}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
